import { useState, useRef, useEffect, useCallback } from 'react';

interface Props {
  src: string;
  onConfirm: (editedDataUrl: string) => void;
  onCancel: () => void;
}

type Tool = 'none' | 'crop' | 'text';
type Filter = 'none' | 'grayscale' | 'sepia' | 'invert' | 'warm' | 'cool';

const FILTERS: { id: Filter; label: string; css: string }[] = [
  { id: 'none',      label: '🌄 Original',  css: '' },
  { id: 'grayscale', label: '⬛ B&N',        css: 'grayscale(1)' },
  { id: 'sepia',     label: '🟫 Sepia',      css: 'sepia(0.8)' },
  { id: 'warm',      label: '🔆 Cálido',     css: 'saturate(1.4) hue-rotate(-15deg) brightness(1.05)' },
  { id: 'cool',      label: '❄️ Frío',       css: 'saturate(0.9) hue-rotate(20deg) brightness(1.02)' },
  { id: 'invert',    label: '🌘 Invertir',   css: 'invert(1)' },
];

export const ImageEditor = ({ src, onConfirm, onCancel }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cleanCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [filter, setFilter] = useState<Filter>('none');
  const [tool, setTool] = useState<Tool>('none');
  const [texts, setTexts] = useState<{ x: number; y: number; text: string; color: string; size: number }[]>([]);
  const [textInput, setTextInput] = useState('');
  const [textColor, setTextColor] = useState('#ffffff');
  const [textSize, setTextSize] = useState(28);
  const [crop, setCrop] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [cropStart, setCropStart] = useState<{ x: number; y: number } | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [activeTab, setActiveTab] = useState<'transform' | 'adjust' | 'filters' | 'text'>('transform');

  useEffect(() => {
    const image = new Image();
    image.onload = () => setImg(image);
    image.src = src;
  }, [src]);

  // Draw clean image (no overlay) to both display canvas and clean canvas
  const drawClean = useCallback((targetCanvas: HTMLCanvasElement, sourceImg: HTMLImageElement) => {
    const ctx = targetCanvas.getContext('2d')!;
    const rad = (rotation * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    targetCanvas.width = Math.round(sourceImg.width * cos + sourceImg.height * sin);
    targetCanvas.height = Math.round(sourceImg.width * sin + sourceImg.height * cos);
    ctx.clearRect(0, 0, targetCanvas.width, targetCanvas.height);
    ctx.save();
    ctx.translate(targetCanvas.width / 2, targetCanvas.height / 2);
    ctx.rotate(rad);
    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
    const filterStr = [
      `brightness(${brightness}%)`,
      `contrast(${contrast}%)`,
      FILTERS.find(f => f.id === filter)?.css || '',
    ].filter(Boolean).join(' ');
    ctx.filter = filterStr || 'none';
    ctx.drawImage(sourceImg, -sourceImg.width / 2, -sourceImg.height / 2);
    ctx.restore();
    // Draw texts
    texts.forEach(t => {
      ctx.font = `bold ${t.size}px sans-serif`;
      ctx.fillStyle = t.color;
      ctx.strokeStyle = 'rgba(0,0,0,0.6)';
      ctx.lineWidth = 3;
      ctx.strokeText(t.text, t.x, t.y);
      ctx.fillText(t.text, t.x, t.y);
    });
  }, [rotation, flipH, flipV, brightness, contrast, filter, texts]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;

    // Draw clean version to hidden canvas
    drawClean(cleanCanvasRef.current, img);

    // Copy clean to display canvas
    canvas.width = cleanCanvasRef.current.width;
    canvas.height = cleanCanvasRef.current.height;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(cleanCanvasRef.current, 0, 0);

    // Draw crop overlay on top (does NOT affect cleanCanvas)
    if (crop) {
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(0, 0, canvas.width, crop.y);
      ctx.fillRect(0, crop.y + crop.h, canvas.width, canvas.height - crop.y - crop.h);
      ctx.fillRect(0, crop.y, crop.x, crop.h);
      ctx.fillRect(crop.x + crop.w, crop.y, canvas.width - crop.x - crop.w, crop.h);
      ctx.strokeStyle = '#7c3aed';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      ctx.strokeRect(crop.x, crop.y, crop.w, crop.h);
      ctx.setLineDash([]);
      const hs = 8;
      ctx.fillStyle = '#7c3aed';
      [[crop.x, crop.y], [crop.x + crop.w - hs, crop.y], [crop.x, crop.y + crop.h - hs], [crop.x + crop.w - hs, crop.y + crop.h - hs]].forEach(([x, y]) => {
        ctx.fillRect(x, y, hs, hs);
      });
    }
  }, [img, crop, drawClean]);

  useEffect(() => { draw(); }, [draw]);

  const getCanvasPos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: Math.round((e.clientX - rect.left) * scaleX),
      y: Math.round((e.clientY - rect.top) * scaleY),
    };
  };

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getCanvasPos(e);
    if (tool === 'crop') { setCropStart(pos); setIsCropping(true); setCrop(null); }
    if (tool === 'text' && textInput.trim()) {
      setTexts(prev => [...prev, { x: pos.x, y: pos.y, text: textInput, color: textColor, size: textSize }]);
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isCropping || !cropStart) return;
    const pos = getCanvasPos(e);
    setCrop({
      x: Math.min(pos.x, cropStart.x),
      y: Math.min(pos.y, cropStart.y),
      w: Math.abs(pos.x - cropStart.x),
      h: Math.abs(pos.y - cropStart.y),
    });
  };

  const handleCanvasMouseUp = () => setIsCropping(false);

  const applyCrop = () => {
    if (!crop || crop.w < 5 || crop.h < 5) return;
    // Use cleanCanvas which has NO overlay
    const clean = cleanCanvasRef.current;
    const out = document.createElement('canvas');
    out.width = crop.w;
    out.height = crop.h;
    out.getContext('2d')!.drawImage(clean, crop.x, crop.y, crop.w, crop.h, 0, 0, crop.w, crop.h);
    const cropped = new Image();
    cropped.onload = () => {
      setImg(cropped);
      setCrop(null);
      setTool('none');
      setRotation(0);
      setFlipH(false);
      setFlipV(false);
    };
    cropped.src = out.toDataURL('image/jpeg', 0.95);
  };

  const handleConfirm = () => {
    // Export from clean canvas
    if (!img) return;
    const clean = cleanCanvasRef.current;
    drawClean(clean, img);
    onConfirm(clean.toDataURL('image/jpeg', 0.92));
  };

  const tabBtn = (id: typeof activeTab, label: string) => (
    <button onClick={() => setActiveTab(id)} style={{ flex: 1, padding: '0.5rem', border: 'none', borderRadius: 8, background: activeTab === id ? '#7c3aed' : 'none', color: activeTab === id ? '#fff' : 'var(--text-dim)', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', fontWeight: 600, transition: 'all 0.15s' }}>
      {label}
    </button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', padding: '1rem' }}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: 0 }}>
        <canvas
          ref={canvasRef}
          style={{ maxWidth: '100%', maxHeight: '60vh', borderRadius: 12, cursor: tool === 'crop' ? 'crosshair' : tool === 'text' ? 'text' : 'default', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
        />
      </div>

      <div style={{ background: 'var(--card-bg)', borderRadius: 20, padding: '1rem', width: '100%', maxWidth: 480, border: '1px solid var(--glass-border)' }}>
        <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.75rem', background: 'var(--input-bg)', borderRadius: 10, padding: '0.2rem' }}>
          {tabBtn('transform', '↻ Transform')}
          {tabBtn('adjust', '☀️ Ajustar')}
          {tabBtn('filters', '🎨 Filtros')}
          {tabBtn('text', '✏️ Texto')}
        </div>

        {activeTab === 'transform' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {([['↺ -90°', () => setRotation(r => r - 90)], ['↻ +90°', () => setRotation(r => r + 90)], ['↔ Voltear H', () => setFlipH(f => !f)], ['↕ Voltear V', () => setFlipV(f => !f)]] as [string, () => void][]).map(([label, fn]) => (
                <button key={label} onClick={fn} style={{ padding: '0.45rem 0.85rem', borderRadius: 8, border: '1px solid var(--glass-border)', background: 'var(--input-bg)', color: 'var(--text-main)', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem' }}>{label}</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
              <button onClick={() => setTool(tool === 'crop' ? 'none' : 'crop')}
                style={{ padding: '0.45rem 1.25rem', borderRadius: 8, border: 'none', background: tool === 'crop' ? '#7c3aed' : 'var(--input-bg)', color: tool === 'crop' ? '#fff' : 'var(--text-main)', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem' }}>
                ✂️ {tool === 'crop' ? 'Seleccionando...' : 'Recortar'}
              </button>
              {crop && <button onClick={applyCrop} style={{ padding: '0.45rem 1.25rem', borderRadius: 8, border: 'none', background: '#22c55e', color: '#fff', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem' }}>✓ Aplicar corte</button>}
            </div>
          </div>
        )}

        {activeTab === 'adjust' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {([['☀️ Brillo', brightness, setBrightness, 50, 200], ['◑ Contraste', contrast, setContrast, 50, 200]] as [string, number, (v: number) => void, number, number][]).map(([label, val, set, min, max]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--text-dim)', width: 90 }}>{label} {val}%</span>
                <input type="range" min={min} max={max} value={val} onChange={e => set(Number(e.target.value))} style={{ flex: 1, accentColor: '#7c3aed' }} />
                <button onClick={() => set(100)} style={{ fontSize: '0.72rem', background: 'none', border: '1px solid var(--glass-border)', borderRadius: 6, color: 'var(--text-dim)', padding: '0.2rem 0.4rem', cursor: 'pointer' }}>↺</button>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'filters' && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            {FILTERS.map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                style={{ padding: '0.45rem 0.75rem', borderRadius: 8, border: 'none', background: filter === f.id ? '#7c3aed' : 'var(--input-bg)', color: filter === f.id ? '#fff' : 'var(--text-main)', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', fontWeight: filter === f.id ? 700 : 400, transition: 'all 0.15s' }}>
                {f.label}
              </button>
            ))}
          </div>
        )}

        {activeTab === 'text' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input value={textInput} onChange={e => setTextInput(e.target.value)} placeholder="Escribe el texto..."
                style={{ flex: 1, background: 'var(--input-bg)', border: '1px solid var(--glass-border)', borderRadius: 8, padding: '0.4rem 0.75rem', color: 'var(--text-main)', fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', outline: 'none' }} />
              <input type="color" value={textColor} onChange={e => setTextColor(e.target.value)} style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer', background: 'none', padding: 0 }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Tamaño: {textSize}px</span>
              <input type="range" min={14} max={72} value={textSize} onChange={e => setTextSize(Number(e.target.value))} style={{ flex: 1, accentColor: '#7c3aed' }} />
            </div>
            <button onClick={() => setTool(tool === 'text' ? 'none' : 'text')}
              style={{ padding: '0.5rem', borderRadius: 8, border: 'none', background: tool === 'text' ? '#7c3aed' : 'var(--input-bg)', color: tool === 'text' ? '#fff' : 'var(--text-main)', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem' }}>
              {tool === 'text' ? '📍 Haz clic en la imagen para colocar' : '✏️ Activar herramienta de texto'}
            </button>
            {texts.length > 0 && (
              <button onClick={() => setTexts([])} style={{ padding: '0.4rem', borderRadius: 8, border: '1px solid #ef4444', background: 'none', color: '#ef4444', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem' }}>
                🗑️ Quitar todos los textos
              </button>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button onClick={onCancel} style={{ padding: '0.65rem 1.75rem', borderRadius: 12, border: '1px solid rgba(255,255,255,0.2)', background: 'none', color: '#fff', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem' }}>
          Cancelar
        </button>
        <button onClick={handleConfirm} style={{ padding: '0.65rem 1.75rem', borderRadius: 12, border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', fontWeight: 700 }}>
          Enviar 📷
        </button>
      </div>
    </div>
  );
};